/**
 * Matching a byline to the account of whoever is signed in.
 *
 * `authorId` is the last field on a new guide that a computer could fill in and
 * did not: the person creating the guide is, overwhelmingly, its author. What
 * stopped it was that `Person` had no join key to a Firebase account. It has one
 * now (`uid`, and `email` as the fallback), and this is the lookup.
 *
 * It has to degrade rather than guess. Team bylines — "Zennopay Editorial",
 * "Zennopay Treasury Desk" — are deliberate and have no account behind them
 * (`docs/roadmap.md`, "Author identities"), so an account with no matching
 * record must produce *nothing* and leave the editor with the picker they
 * already had. Guessing by name would attribute a guide to the wrong person,
 * and attribution is exactly the thing this field exists to get right.
 *
 * Kept free of React and of Firebase so it can be tested as a function.
 */

/** The subset of `Person` this lookup reads, so a partial record is enough. */
export interface AccountLinkedPerson {
    id: string;
    uid?: string;
    email?: string;
}

/** The subset of a Firebase user this lookup reads. */
export interface SignedInAccount {
    uid?: string;
    email?: string;
}

const normalizeEmail = (value: string | undefined): string | undefined => {
    const trimmed = (value ?? "").trim().toLowerCase();
    return trimmed === "" ? undefined : trimmed;
};

const normalizeUid = (value: string | undefined): string | undefined => {
    const trimmed = (value ?? "").trim();
    return trimmed === "" ? undefined : trimmed;
};

/**
 * The person record belonging to this account, or `undefined`.
 *
 * `uid` wins over `email` because it is the identity Firebase actually
 * authenticates: an email address can be reassigned to a new employee, and a
 * stale `email` on an old byline should not quietly claim the new person's
 * guides. Email is still consulted, because it is the field an admin can fill
 * in from the CMS without going to look up an opaque uid.
 *
 * Ties are impossible to resolve honestly, so the first match wins and the
 * caller says which record it chose.
 */
export function findPersonForAccount<T extends AccountLinkedPerson>(
    people: readonly T[],
    account: SignedInAccount
): T | undefined {
    const uid = normalizeUid(account.uid);
    const email = normalizeEmail(account.email);
    if (!uid && !email) return undefined;

    if (uid) {
        const byUid = people.find((person) => normalizeUid(person.uid) === uid);
        if (byUid) return byUid;
    }
    if (email) {
        const byEmail = people.find((person) => normalizeEmail(person.email) === email);
        if (byEmail) return byEmail;
    }
    return undefined;
}
